/**
 * Hybrid Radio Component
 * Automatically chooses between Tailwind and styled-components based on props
 */

import * as React from "react";
import Container, { Input, InputWrapper, LabelText } from "./styles"; // Styled-components version
import RadioTailwind from "./RadioTailwind"; // Tailwind version
import type { TypeRadioProps } from "./RadioTypes";
import { needsStyledComponents } from "@sproutsocial/seeds-react-system-props";

const RadioHybrid = React.forwardRef<HTMLInputElement, TypeRadioProps>(
  (props, ref) => {
    const {
      id,
      value,
      name,
      label,
      checked,
      disabled = false,
      onChange,
      onFocus,
      onBlur,
      ariaLabel,
      qa = {},
      color,
      tabIndex,
      ...rest
    } = props;

    // Mirror the class component's guarded handlers: onChange only fires for an
    // unchecked, enabled input; focus/blur are simple pass-throughs.
    const handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
      if (!!checked || disabled) {
        return;
      }
      onChange && onChange(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e);
      }
    };

    // Styled-components path: any styled-system prop present (incl. color) or a
    // styled(Radio) extension (which injects a `theme` prop) renders the exact
    // composition the class component rendered previously.
    if (needsStyledComponents(props)) {
      return (
        <Container
          as={label && "label"}
          // TODO: fix this type since `color` should be valid here. TS can't resolve the correct type.
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          color={color}
          {...rest}
        >
          <InputWrapper checked={checked} disabled={disabled}>
            <Input
              ref={ref}
              type="radio"
              id={id}
              tabIndex={tabIndex}
              aria-label={ariaLabel}
              value={value}
              name={name}
              checked={checked}
              disabled={disabled}
              onChange={handleChange}
              onFocus={handleFocus}
              onBlur={handleBlur}
              data-qa-radio={name || ""}
              data-qa-radio-ischecked={checked === true}
              data-qa-radio-isdisabled={disabled === true}
              {...qa}
            />
          </InputWrapper>
          {label && <LabelText disabled={disabled}>{label}</LabelText>}
        </Container>
      );
    }

    // Use Tailwind version (preferred - better performance)
    return <RadioTailwind {...props} ref={ref} />;
  }
);

RadioHybrid.displayName = "Radio";
export default RadioHybrid;
